import { z, type ZodTypeAny } from "zod";

/**
 * Validation configuration that lives on an assessment block's definition data
 * under the `validation` key.  Configured by the global "Validation" editor.
 *
 * The shape intentionally mirrors zod primitives so it is easy to translate
 * into a `ZodSchema` at runtime via {@link buildZodSchemaFromConfig}.
 */
export type ValidationInputType = "string" | "number" | "boolean" | "array";

export interface ValidationRequiredRule {
  enabled: boolean;
  message?: string;
}

export interface ValidationMinMaxRule {
  value: number;
  message?: string;
}

export interface ValidationPatternRule {
  regex: string;
  flags?: string;
  message?: string;
}

export interface ValidationConfig {
  required?: ValidationRequiredRule;
  type?: ValidationInputType;
  min?: ValidationMinMaxRule;
  max?: ValidationMinMaxRule;
  pattern?: ValidationPatternRule;
}

/** Default empty configuration used when a block has no validation set. */
export const emptyValidationConfig: ValidationConfig = {
  required: { enabled: false, message: "" },
};

/**
 * Map a component definition id to the input type used for building the
 * zod schema and to filter which validation rules are relevant in the
 * validation editor.
 */
export function inferInputTypeFromComponentId(
  componentId: string | undefined
): ValidationInputType {
  if (!componentId) return "string";
  if (componentId.includes("checkbox-group")) return "array";
  if (componentId.includes("number-input")) return "number";
  return "string";
}

/**
 * Component ids that may receive a validation configuration. Used by the
 * global commands composable to gate the "Edit validation" command.
 */
export const VALIDATABLE_COMPONENT_IDS = [
  "application/vnd.pivotalgroup.component.text-input+json",
  "application/vnd.pivotalgroup.component.number-input+json",
  "application/vnd.pivotalgroup.component.checkbox-group+json",
  "application/vnd.pivotalgroup.component.radio-group+json",
  "application/vnd.pivotalgroup.component.select-input+json",
] as const;

export function isValidatableComponentId(componentId: string | undefined) {
  if (!componentId) return false;
  return (VALIDATABLE_COMPONENT_IDS as readonly string[]).includes(componentId);
}

/** Compile a zod schema from the stored validation config. */
export function buildZodSchemaFromConfig(
  config: ValidationConfig | undefined,
  inputType: ValidationInputType
): ZodTypeAny {
  const cfg = config ?? {};
  const effectiveType: ValidationInputType = cfg.type ?? inputType;

  let schema: ZodTypeAny;
  switch (effectiveType) {
    case "number": {
      let n = z.preprocess((v) => {
        if (v === "" || v === null || v === undefined) return undefined;
        if (typeof v === "number") return v;
        const parsed = Number(v);
        return Number.isNaN(parsed) ? v : parsed;
      }, z.number({ invalid_type_error: "Must be a number" }));
      if (cfg.min) {
        n = (n as any).pipe(
          z.number().min(cfg.min.value, { message: cfg.min.message || `Must be at least ${cfg.min.value}` })
        );
      }
      if (cfg.max) {
        n = (n as any).pipe(
          z.number().max(cfg.max.value, { message: cfg.max.message || `Must be at most ${cfg.max.value}` })
        );
      }
      schema = n;
      break;
    }
    case "array": {
      let a: ZodTypeAny = z.array(z.any());
      if (cfg.min) {
        a = (a as any).min(cfg.min.value, {
          message: cfg.min.message || `Select at least ${cfg.min.value}`,
        });
      }
      if (cfg.max) {
        a = (a as any).max(cfg.max.value, {
          message: cfg.max.message || `Select at most ${cfg.max.value}`,
        });
      }
      schema = a;
      break;
    }
    case "boolean":
      schema = z.boolean();
      break;
    case "string":
    default: {
      let s: ZodTypeAny = z.string();
      if (cfg.min) {
        s = (s as any).min(cfg.min.value, {
          message: cfg.min.message || `Must be at least ${cfg.min.value} characters`,
        });
      }
      if (cfg.max) {
        s = (s as any).max(cfg.max.value, {
          message: cfg.max.message || `Must be at most ${cfg.max.value} characters`,
        });
      }
      if (cfg.pattern && cfg.pattern.regex) {
        try {
          const rx = new RegExp(cfg.pattern.regex, cfg.pattern.flags || undefined);
          s = (s as any).regex(rx, {
            message: cfg.pattern.message || "Invalid format",
          });
        } catch {
          // ignore invalid regex from author; treated as no pattern rule
        }
      }
      schema = s;
      break;
    }
  }

  if (cfg.required?.enabled) {
    const requiredMessage = cfg.required.message || "This field is required";
    if (effectiveType === "string") {
      schema = (schema as any).min(1, { message: requiredMessage });
    } else if (effectiveType === "array") {
      schema = (schema as any).min(1, { message: requiredMessage });
    } else {
      schema = (schema as any).refine(
        (v: unknown) => v !== undefined && v !== null && v !== "",
        { message: requiredMessage }
      );
    }
  } else {
    schema = schema.optional().or(z.literal(""));
  }

  return schema;
}

/**
 * Validate a single value against the supplied validation config. Returns
 * `{ ok: true }` on success, or `{ ok: false, message }` on the first failure.
 */
export function validateValue(
  value: unknown,
  config: ValidationConfig | undefined,
  inputType: ValidationInputType
): { ok: true } | { ok: false; message: string } {
  const schema = buildZodSchemaFromConfig(config, inputType);
  const result = schema.safeParse(value);
  if (result.success) return { ok: true };
  const first = result.error.issues[0];
  return { ok: false, message: first?.message || "Invalid value" };
}
